
import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { User, UserRole, NewUserData, UpdateUserData } from '../types';
import { DocumentPlusIcon } from '../components/icons/DocumentPlusIcon';
import { EditIcon } from '../components/icons/EditIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { UserIcon } from '../components/icons/UserIcon';
import { UserFormModal } from '../components/UserFormModal';
import { DeleteUserModal } from '../components/DeleteUserModal';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../hooks/useAuth';
import { Input } from '../components/ui/Input';
import { SearchIcon } from '../components/icons/SearchIcon';

export const CadastrarUsuarioPage: React.FC = () => {
  const { user: loggedInUser } = useAuth();
  const { users, addUser, updateUser, deleteUser } = useUsers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Ordenação absoluta A-Z pelo nome, ignorando espaços e tratando acentos
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = (a.name || '').trim();
      const nameB = (b.name || '').trim();
      return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    let usersToDisplay = sortedUsers;
    
    // Ocultar usuários DEV para quem não é DEV
    if (loggedInUser?.role !== UserRole.DEV) {
      usersToDisplay = usersToDisplay.filter(user => user.role !== UserRole.DEV);
    }
    
    if (!searchTerm) {
        return usersToDisplay;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return usersToDisplay.filter(user =>
        (user.name || '').toLowerCase().includes(lowercasedSearchTerm) ||
        (user.email || '').toLowerCase().includes(lowercasedSearchTerm) ||
        (user.phone && user.phone.includes(searchTerm))
    );
  }, [sortedUsers, searchTerm, loggedInUser]);

  const handleOpenAddModal = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleOpenDeleteModal = (user: User) => {
    setUserToDelete(user);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleCloseDeleteModal = () => {
    setUserToDelete(null);
  };

  const handleSaveUser = (data: NewUserData | UpdateUserData) => {
    if (editingUser) {
      updateUser(editingUser.id, data as UpdateUserData);
    } else {
      addUser(data as NewUserData);
    }
    handleCloseModal();
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser(userToDelete.id);
      handleCloseDeleteModal();
    }
  };

  const getRoleLabel = (role: UserRole) => {
    if (role === UserRole.DEV) return 'Desenvolvedor';
    return role === UserRole.ADMIN ? 'Administrador' : 'Funcionário';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Funcionários</h1>
        <Button
          variant="primary"
          onClick={handleOpenAddModal}
          leftIcon={<DocumentPlusIcon className="text-xl" />}
        >
          Adicionar Usuário
        </Button>
      </div>
      
      <div className="mb-4">
        <div className="relative w-full md:max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="text-xl text-gray-400" />
            </div>
            <Input
                id="searchUsers"
                type="text"
                placeholder="Pesquisar por nome, e-mail ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
                containerClassName="mb-0"
            />
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-tertiary">
            <thead className="bg-gray-50 dark:bg-dark-secondary">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Usuário</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Telefone</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Perfil</th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-tertiary">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-secondary/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.avatarUrl ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={user.avatarUrl} alt={user.name} />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-dark-tertiary flex items-center justify-center">
                            <UserIcon className="text-2xl text-gray-500 dark:text-dark-text-secondary" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary uppercase">{user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-text-secondary">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-dark-text-secondary">{user.phone || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === UserRole.DEV
                        ? 'bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200'
                        : user.role === UserRole.ADMIN
                        ? 'bg-brand-teal bg-opacity-30 text-brand-lime'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                    }`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(user)} className="p-1" title="Editar">
                      <EditIcon className="text-xl"/>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDeleteModal(user)}
                      className="p-1 text-red-500"
                      title="Excluir"
                      disabled={user.id === loggedInUser?.id || user.id === 'admin001' || user.id === 'dev001'}
                    >
                      <TrashIcon className="text-xl"/>
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                  <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-gray-500 dark:text-dark-text-secondary">Nenhum funcionário encontrado.</td>
                  </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {isModalOpen && (
        <UserFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSaveUser}
          initialUser={editingUser}
        />
      )}

      {userToDelete && (
        <DeleteUserModal
          isOpen={!!userToDelete}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          userName={userToDelete.name}
        />
      )}
    </div>
  );
};
